/**
 * TechnicianPieChart Component
 *
 * Pie/Donut chart showing labor gross distribution among technicians.
 * Uses teal-based color palette.
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Technician } from './types';

interface TechnicianPieChartProps {
  technicians: Technician[];
  totalLaborGross: number;
}

const COLORS = [
  '#0d9488', // teal-600
  '#14b8a6', // teal-500
  '#2dd4bf', // teal-400
  '#5eead4', // teal-300
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#10b981', // emerald-500
  '#34d399', // emerald-400
  '#6ee7b7', // emerald-300
  '#a78bfa', // violet-400
];

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{data.name}</p>
        <p className="text-sm text-gray-600">
          Labor Gross:{' '}
          <span className="font-medium text-teal-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        <p className="text-sm text-gray-600">
          FRH: <span className="font-medium">{data.frh.toFixed(1)}</span>
        </p>
      </div>
    );
  }
  return null;
};

const renderLegendText = (value: string) => {
  const maxLength = 15;
  if (value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  return value;
};

export const TechnicianPieChart: React.FC<TechnicianPieChartProps> = ({
  technicians,
  totalLaborGross,
}) => {
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (technicians.length === 0 || totalLaborGross === 0) {
      return [];
    }

    const sorted = [...technicians].sort(
      (a, b) => b.laborGrossTotal - a.laborGrossTotal
    );

    if (sorted.length <= MAX_SLICES) {
      return sorted.map((tech, index) => ({
        name: tech.techName,
        value: tech.laborGrossTotal,
        percentage: (tech.laborGrossTotal / totalLaborGross) * 100,
        frh: tech.flatRateHoursTotal,
        color: COLORS[index % COLORS.length],
      }));
    }

    const top = sorted.slice(0, MAX_SLICES - 1);
    const others = sorted.slice(MAX_SLICES - 1);
    const othersTotal = others.reduce((sum, t) => sum + t.laborGrossTotal, 0);
    const othersFrh = others.reduce((sum, t) => sum + t.flatRateHoursTotal, 0);

    const result = top.map((tech, index) => ({
      name: tech.techName,
      value: tech.laborGrossTotal,
      percentage: (tech.laborGrossTotal / totalLaborGross) * 100,
      frh: tech.flatRateHoursTotal,
      color: COLORS[index % COLORS.length],
    }));

    if (othersTotal > 0) {
      result.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        percentage: (othersTotal / totalLaborGross) * 100,
        frh: othersFrh,
        color: '#9ca3af',
      });
    }

    return result;
  }, [technicians, totalLaborGross]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={renderLegendText}
            wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
